import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiStar, FiChevronLeft, FiChevronRight } = FiIcons;

const Testimonials = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [visibleCount, setVisibleCount] = useState(3);

  // Responsive visible count
  useEffect(() => {
    const updateVisibleCount = () => {
      if (window.innerWidth < 640) {
        setVisibleCount(1);
      } else if (window.innerWidth < 1024) {
        setVisibleCount(2);
      } else {
        setVisibleCount(3);
      }
    };
    
    updateVisibleCount();
    window.addEventListener('resize', updateVisibleCount);
    return () => window.removeEventListener('resize', updateVisibleCount);
  }, []);

  const testimonials = [
    {
      name: "Sarah Johnson",
      company: "TechStart Solutions",
      text: "BPA Solutions team transformed our appointment booking system completely. We went from manual scheduling chaos to a streamlined automated process. Our no-shows decreased by 60% and customer satisfaction skyrocketed!",
      rating: 5,
      location: "Sydney, NSW"
    },
    {
      name: "Michael Chen",
      company: "Growth Marketing Co.",
      text: "The BPA Solutions team implemented an AI chatbot that handles 80% of our customer inquiries automatically. The response time improvement has been incredible, and our customers love the instant 24/7 support.",
      rating: 5,
      location: "Melbourne, VIC"
    },
    {
      name: "Emma Rodriguez",
      company: "Legal Associates",
      text: "BPA Solutions' document generation system saved us countless hours every week. What used to take days now happens in minutes. The consistency and accuracy are outstanding - it's transformed our practice.",
      rating: 5,
      location: "Brisbane, QLD"
    },
    {
      name: "David Wilson",
      company: "Retail Plus",
      text: "The BPA Solutions team set up our CRM automation and it's been a complete game-changer. Our lead conversion rate increased by 45% and we never miss a follow-up anymore. ROI was immediate.",
      rating: 5,
      location: "Perth, WA"
    },
    {
      name: "Jennifer Lee",
      company: "Consulting Group",
      text: "BPA Solutions' Microsoft 365 automation integrated perfectly with our existing workflow. Team collaboration has improved dramatically and we've eliminated so many manual processes.",
      rating: 5,
      location: "Adelaide, SA"
    },
    {
      name: "Robert Taylor",
      company: "Manufacturing Inc.",
      text: "The custom process automation BPA Solutions developed for our inventory management is phenomenal. We've reduced errors by 90% and increased efficiency by 200%. Best investment we've made.",
      rating: 5,
      location: "Newcastle, NSW"
    },
    {
      name: "Lisa Anderson",
      company: "Healthcare Solutions",
      text: "BPA Solutions' online forms and survey system streamlined our patient intake process completely. The integration with our existing systems was seamless and patient experience improved significantly.",
      rating: 5,
      location: "Gold Coast, QLD"
    },
    {
      name: "James Parker",
      company: "Financial Services",
      text: "BPA Solutions' scanning and OCR automation digitized our entire document archive efficiently. The search and retrieval capabilities have revolutionized our operations and compliance reporting.",
      rating: 5,
      location: "Canberra, ACT"
    },
    {
      name: "Amanda Foster",
      company: "E-commerce Pro",
      text: "BPA Solutions delivered a stunning website with integrated booking system. Our online conversions increased by 150% within the first month. The design is professional and converts visitors perfectly.",
      rating: 5,
      location: "Wollongong, NSW"
    },
    {
      name: "Thomas Martinez",
      company: "Property Management",
      text: "BPA Solutions' automation for our property management workflows has saved us 20+ hours per week. The ROI was immediate and substantial. Our tenants and owners love the automated processes.",
      rating: 5,
      location: "Darwin, NT"
    },
    {
      name: "Rachel Green",
      company: "Marketing Agency",
      text: "The AI chatbot BPA Solutions implemented has become our best sales assistant. It qualifies leads 24/7 automatically and our conversion rate has never been higher. Exceptional service and results.",
      rating: 5,
      location: "Hobart, TAS"
    },
    {
      name: "Kevin Brown",
      company: "Insurance Brokers",
      text: "BPA Solutions' document generation system for our insurance forms has eliminated errors completely and reduced processing time by 80%. Absolutely fantastic work and ongoing support.",
      rating: 5,
      location: "Cairns, QLD"
    }
  ];

  // Auto-scroll functionality
  useEffect(() => {
    if (!isAutoPlaying) return;

    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => 
        prevIndex === testimonials.length - visibleCount ? 0 : prevIndex + 1
      );
    }, 4000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, testimonials.length, visibleCount]);

  const nextTestimonial = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === testimonials.length - visibleCount ? 0 : prevIndex + 1
    );
  };

  const prevTestimonial = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === 0 ? testimonials.length - visibleCount : prevIndex - 1
    );
  };

  const getVisibleTestimonials = () => {
    const visible = [];
    for (let i = 0; i < visibleCount; i++) {
      const index = (currentIndex + i) % testimonials.length;
      visible.push(testimonials[index]);
    }
    return visible;
  };

  return (
    <section className="py-12 md:py-16 lg:py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-10 md:mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 md:mb-6">
            What Australian <span className="text-primary-600">Businesses Say</span>
          </h2>
          <div className="w-16 md:w-20 h-1 bg-primary-600 mx-auto mb-4 md:mb-6"></div>
          <p className="text-base md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Don't just take our word for it. Here's what businesses across Australia are saying about our automation solutions.
          </p>
        </motion.div>

        {/* Testimonials Carousel */}
        <div className="relative">
          <div
            className="flex transition-transform duration-500 ease-in-out"
            onMouseEnter={() => setIsAutoPlaying(false)}
            onMouseLeave={() => setIsAutoPlaying(true)}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-8 w-full">
              {getVisibleTestimonials().map((testimonial, index) => (
                <motion.div
                  key={`${currentIndex}-${index}`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="bg-white rounded-xl md:rounded-2xl shadow-md md:shadow-lg p-5 md:p-8 hover:shadow-xl transition-shadow h-full"
                >
                  {/* Stars */}
                  <div className="flex items-center mb-3 md:mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <SafeIcon key={i} icon={FiStar} className="h-4 w-4 md:h-5 md:w-5 text-yellow-400 fill-current" />
                    ))}
                  </div>

                  {/* Testimonial Text */}
                  <p className="text-sm md:text-base text-gray-600 mb-4 md:mb-6 leading-relaxed italic line-clamp-4 sm:line-clamp-none">
                    "{testimonial.text}"
                  </p>

                  {/* Client Info */}
                  <div className="border-t pt-3 md:pt-4">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div>
                        <h4 className="text-sm md:text-base font-semibold text-gray-900">{testimonial.name}</h4>
                        <p className="text-xs md:text-sm text-gray-500">{testimonial.company}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs md:text-sm font-medium text-primary-600">Location</p>
                        <p className="text-xs md:text-sm text-gray-700">{testimonial.location}</p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Navigation Buttons */}
          <button
            onClick={prevTestimonial}
            className="absolute left-0 top-1/2 transform -translate-y-1/2 -translate-x-2 md:-translate-x-4 bg-white shadow-md md:shadow-lg rounded-full p-2 md:p-3 hover:bg-gray-50 transition-colors z-10"
            aria-label="Previous testimonial"
          >
            <SafeIcon icon={FiChevronLeft} className="h-5 w-5 md:h-6 md:w-6 text-gray-600" />
          </button>
          <button
            onClick={nextTestimonial}
            className="absolute right-0 top-1/2 transform -translate-y-1/2 translate-x-2 md:translate-x-4 bg-white shadow-md md:shadow-lg rounded-full p-2 md:p-3 hover:bg-gray-50 transition-colors z-10"
            aria-label="Next testimonial"
          >
            <SafeIcon icon={FiChevronRight} className="h-5 w-5 md:h-6 md:w-6 text-gray-600" />
          </button>
        </div>

        {/* Dots Indicator */}
        <div className="flex justify-center mt-6 md:mt-8 space-x-1 md:space-x-2">
          {testimonials.slice(0, testimonials.length - visibleCount + 1).map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentIndex(index)}
              className={`w-2 h-2 md:w-3 md:h-3 rounded-full transition-colors ${
                index === currentIndex ? 'bg-primary-600' : 'bg-gray-300'
              }`}
              aria-label={`Go to testimonial ${index + 1}`}
            />
          ))}
        </div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8 mt-12 md:mt-16 text-center"
        >
          <div>
            <div className="text-2xl md:text-4xl font-bold text-primary-600 mb-1 md:mb-2">500+</div>
            <div className="text-sm md:text-base text-gray-600">Happy Clients</div>
          </div>
          <div>
            <div className="text-2xl md:text-4xl font-bold text-primary-600 mb-1 md:mb-2">99%</div>
            <div className="text-sm md:text-base text-gray-600">Satisfaction Rate</div>
          </div>
          <div>
            <div className="text-2xl md:text-4xl font-bold text-primary-600 mb-1 md:mb-2">1000+</div>
            <div className="text-sm md:text-base text-gray-600">Projects Completed</div>
          </div>
          <div>
            <div className="text-2xl md:text-4xl font-bold text-primary-600 mb-1 md:mb-2">24/7</div>
            <div className="text-sm md:text-base text-gray-600">Support Available</div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Testimonials;